
import React from 'react';
import { 
  Card, 
  CardContent, 
  CardFooter,
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { BookOpenCheck, DollarSign, Calendar, AlertCircle } from "lucide-react";
import { format, isValid } from "date-fns";
import { Student } from "@/services/db/types";
import { formatCurrency } from "@/utils/formatters";

export interface StudentSummaryCardProps {
  student: Student;
  calculateTotalFees: () => number;
}

// Safe date formatter that handles invalid dates
const safeFormat = (date: string | Date, formatStr: string): string => {
  if (!date) return "Not specified";
  
  try {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return isValid(dateObj) ? format(dateObj, formatStr) : "Invalid date";
  } catch (error) {
    console.error("Date formatting error:", error);
    return "Invalid date";
  }
};

const StudentSummaryCard = ({ student, calculateTotalFees }: StudentSummaryCardProps) => {
  return (
    <Card>
      <CardHeader className="bg-nalc-teal/10 dark:bg-nalc-teal/20">
        <CardTitle className="text-lg">Student Summary</CardTitle>
      </CardHeader>
      <CardContent className="pt-6">
        <div className="space-y-4">
          <div className="flex items-center">
            <BookOpenCheck className="h-5 w-5 mr-3 text-nalc-teal" />
            <div>
              <p className="text-sm text-muted-foreground">Total Programs</p>
              <p className="font-medium">{student.programs.length}</p>
            </div>
          </div>
          
          <div className="flex items-center">
            <DollarSign className="h-5 w-5 mr-3 text-nalc-teal" />
            <div>
              <p className="text-sm text-muted-foreground">Total Fees</p>
              <p className="font-medium">{formatCurrency(calculateTotalFees())}</p>
            </div>
          </div>
          
          <div className="flex items-center">
            <Calendar className="h-5 w-5 mr-3 text-nalc-teal" />
            <div>
              <p className="text-sm text-muted-foreground">Added On</p>
              <p className="font-medium">{safeFormat(student.createdAt, "PPP")}</p>
            </div>
          </div>
          
          {/* Add Fee Status Summary */}
          <div className="mt-6 pt-4 border-t">
            <h4 className="font-medium mb-2">Fee Status Summary</h4>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="bg-green-50 dark:bg-green-900/20 p-2 rounded">
                <span className="text-sm block text-green-800 dark:text-green-400 font-medium">Paid</span>
                <span className="text-lg font-semibold">
                  {student.programs.filter(p => p.feeStatus === 'paid').length}
                </span>
              </div>
              <div className="bg-yellow-50 dark:bg-yellow-900/20 p-2 rounded">
                <span className="text-sm block text-yellow-800 dark:text-yellow-400 font-medium">Partial</span>
                <span className="text-lg font-semibold">
                  {student.programs.filter(p => p.feeStatus === 'partial').length}
                </span>
              </div>
              <div className="bg-red-50 dark:bg-red-900/20 p-2 rounded">
                <span className="text-sm block text-red-800 dark:text-red-400 font-medium">Pending</span>
                <span className="text-lg font-semibold">
                  {student.programs.filter(p => p.feeStatus === 'pending').length}
                </span>
              </div>
            </div>
          </div>
          
          {/* Check for upcoming payments with safe date handling */}
          {student.programs.some(p => p.feeStatus !== 'paid' && p.nextPaymentDate) && (
            <div className="mt-2 bg-amber-50 dark:bg-amber-900/20 p-3 rounded">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 mr-2" />
                <div>
                  <h5 className="font-medium text-amber-800 dark:text-amber-300">Upcoming Payments</h5>
                  <ul className="text-sm space-y-1 mt-1">
                    {student.programs
                      .filter(p => p.feeStatus !== 'paid' && p.nextPaymentDate)
                      .map((program, i) => {
                        // Check if date is valid before rendering
                        try {
                          const paymentDate = new Date(program.nextPaymentDate!);
                          if (!isValid(paymentDate)) return null;
                          
                          return (
                            <li key={i} className="text-amber-700 dark:text-amber-200">
                              {program.title} - Due {safeFormat(program.nextPaymentDate!, "PPP")}
                            </li>
                          );
                        } catch (e) {
                          return null;
                        }
                      })
                      .filter(Boolean) // Remove null items
                    }
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      </CardContent>
      <CardFooter className="flex flex-col items-start bg-slate-50 dark:bg-nalc-dark/30 rounded-b-lg px-6 py-4">
        <p className="text-sm font-medium">Last Updated</p>
        <p className="text-sm text-muted-foreground">
          {safeFormat(student.updatedAt, "PPP 'at' p")}
        </p>
      </CardFooter>
    </Card>
  );
};

export default StudentSummaryCard;
